import React, { useContext, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Tag01Icon,
  UserCircleIcon,
  Logout01Icon,
  Login01Icon,
  Invoice01Icon,
  WhatsappIcon,
} from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/hooks";
import { userSelector, isLoggedInSelector, logOut } from "@/features/auth/slice";
import { openPhoneLoginDrawer } from "@/store/uiSlice";
import routes from "@/constants/routes";
import useSnackbar from "@/hooks/useSnackbar";
import { stringToColor, getInitials } from "@/utils/names";
import { formatPhone } from "@/utils/phones";
import { WebsiteContext } from "@/pages/website/WebsiteProvider";
import PhoneLoginDrawer from "@/components/loyalty/PhoneLoginDrawer";
import { Order } from "@/types";
import OrderHistoryDrawer from "./OrderHistoryDrawer";

interface Props {
  orderNumber?: number;
  order?: Order;
  children: React.ReactNode;
}

const OrderStatusLayout = ({ orderNumber, order, children }: Props) => {
  const { palette } = useTheme();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [openSnackbar] = useSnackbar();
  const { organizationAlias, orderId } = useParams<{ organizationAlias?: string; orderId?: string }>();
  const { organization, branch } = useContext(WebsiteContext);
  const activeOrgId = useAppSelector((state) => state.ui.activeOrganization);
  const organizationId = organization?.id || activeOrgId;

  const user = useAppSelector(userSelector);
  const isLoggedInRedux = useAppSelector(isLoggedInSelector);
  const isLoggedIn = isLoggedInRedux || Boolean(user && user.uid && (user.phone || user.email || user.name));

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isHistoryDrawerOpen, setIsHistoryDrawerOpen] = useState(false);

  const isMenuOpen = Boolean(anchorEl);

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleGoToProfile = () => {
    handleCloseMenu();
    const profileRoute = organizationAlias
      ? routes.organizations.customerProfile.main.replace(":organizationAlias", organizationAlias)
      : routes.customerProfile;
    navigate(profileRoute);
  };

  const handleOpenHistory = () => {
    handleCloseMenu();
    setIsHistoryDrawerOpen(true);
  };

  const handleLogin = () => {
    handleCloseMenu();
    dispatch(openPhoneLoginDrawer());
  };

  const handleLogout = async () => {
    handleCloseMenu();
    await dispatch(logOut());
    openSnackbar("Sesión cerrada correctamente", "info");
  };

  const handleChat = () => {
    const selectedBranch = branch?.find((b) => b.id === order?.branch?.id);
    const whatsapp = selectedBranch?.whatsapp || organization?.whatsapp;

    if (!whatsapp) {
      console.error("No hay WhatsApp disponible para esta orden");
      return;
    }

    const message = `Hola, necesito ayuda con mi pedido N° ${order?.number ?? orderNumber ?? ""}`;
    const text = encodeURIComponent(message);
    window.open(`https://wa.me/${whatsapp}?text=${text}`, "_blank");
  };

  const fullName = `${user?.name || ""} ${user?.lastname || ""}`.trim();
  const initials = fullName ? getInitials(fullName) : "";

  return (
    <Stack height={"100%"} sx={{ backgroundColor: "#DDEAFD" }}>
      <Paper square elevation={1}>
        <Stack direction={"row"} alignItems={"center"} spacing={2} px={2} py={2.5}>
          <Typography variant="body1" fontWeight={500}>
            Estatus del pedido
          </Typography>
          {orderNumber ? (
            <Stack direction="row" alignItems={"center"} spacing={1}>
              <HugeiconsIcon icon={Tag01Icon} color={palette.primary.main} size={20} />
              <Typography variant="body1" fontWeight={500}>
                {orderNumber}
              </Typography>
            </Stack>
          ) : null}

          {/* User Icon Button */}
          <Stack flex={1} alignItems={"flex-end"}>
            <IconButton
              onClick={handleOpenMenu}
              size="small"
              sx={{
                p: 0.5,
                transition: "transform 0.2s ease",
                "&:hover": { transform: "scale(1.05)" },
              }}
              aria-label="opciones de usuario"
              aria-controls={isMenuOpen ? "user-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={isMenuOpen ? "true" : undefined}
            >
              {isLoggedIn && fullName ? (
                <Avatar
                  src={user?.image}
                  sx={{
                    bgcolor: stringToColor(fullName),
                    color: "#fff",
                    width: 40,
                    height: 40,
                    fontSize: "0.95rem",
                    fontWeight: 700,
                  }}
                >
                  {initials}
                </Avatar>
              ) : (
                <Avatar
                  sx={{
                    bgcolor: isLoggedIn ? palette.primary.main : palette.grey[200],
                    color: isLoggedIn ? "#fff" : palette.grey[700],
                    width: 40,
                    height: 40,
                  }}
                >
                  <HugeiconsIcon icon={UserCircleIcon} size={24} />
                </Avatar>
              )}
            </IconButton>
          </Stack>
        </Stack>
      </Paper>

      {/* User Options Dropdown Menu */}
      <Menu
        id="user-menu"
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={handleCloseMenu}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        slotProps={{
          paper: {
            elevation: 4,
            sx: {
              minWidth: 240,
              borderRadius: 3,
              mt: 1,
              py: 0.5,
              overflow: "visible",
              boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.12)",
              "& .MuiMenuItem-root": {
                px: 2,
                py: 1.2,
                borderRadius: 1.5,
                mx: 1,
                my: 0.3,
                fontSize: "0.9rem",
              },
            },
          },
        }}
      >
        {isLoggedIn ? (
          <>
            {/* Logged-in User Info Header */}
            <Box px={2.5} py={1.5}>
              <Typography variant="subtitle2" fontWeight={700} noWrap>
                {fullName || "Mi Cuenta"}
              </Typography>
              {(user?.phone || user?.email) && (
                <Typography variant="caption" color="text.secondary" noWrap display="block">
                  {user?.phone ? formatPhone(user.phone) : user?.email}
                </Typography>
              )}
            </Box>
            <Divider sx={{ my: 0.5 }} />

            {/* Datos personales / Customer Profile */}
            <MenuItem onClick={handleGoToProfile}>
              <ListItemIcon sx={{ color: "primary.main", minWidth: 36 }}>
                <HugeiconsIcon icon={UserCircleIcon} size={20} />
              </ListItemIcon>
              <ListItemText
                primary="Datos personales"
                primaryTypographyProps={{ variant: "body2", fontWeight: 500 }}
              />
            </MenuItem>

            {/* Historial de pedidos */}
            <MenuItem onClick={handleOpenHistory}>
              <ListItemIcon sx={{ color: "primary.main", minWidth: 36 }}>
                <HugeiconsIcon icon={Invoice01Icon} size={20} />
              </ListItemIcon>
              <ListItemText
                primary="Historial de pedidos"
                primaryTypographyProps={{ variant: "body2", fontWeight: 500 }}
              />
            </MenuItem>

            <Divider sx={{ my: 0.5 }} />

            {/* Cerrar sesión */}
            <MenuItem onClick={handleLogout} sx={{ color: "error.main" }}>
              <ListItemIcon sx={{ color: "inherit", minWidth: 36 }}>
                <HugeiconsIcon icon={Logout01Icon} size={20} />
              </ListItemIcon>
              <ListItemText
                primary="Cerrar sesión"
                primaryTypographyProps={{ variant: "body2", fontWeight: 500 }}
              />
            </MenuItem>
          </>
        ) : (
          <>
            {/* Non-logged in Header */}
            <Box px={2.5} py={1.5}>
              <Typography variant="subtitle2" fontWeight={700}>
                Bienvenido
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                Inicia sesión para ver tu perfil y pedidos
              </Typography>
            </Box>
            <Divider sx={{ my: 0.5 }} />

            {/* Iniciar sesión */}
            <MenuItem onClick={handleLogin}>
              <ListItemIcon sx={{ color: "primary.main", minWidth: 36 }}>
                <HugeiconsIcon icon={Login01Icon} size={20} />
              </ListItemIcon>
              <ListItemText
                primary="Iniciar sesión"
                primaryTypographyProps={{ variant: "body2", fontWeight: 600 }}
              />
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Main Content */}
      <Stack flex={1} p={2} spacing={2}>
        {children}
      </Stack>
      <Stack justifyContent="center" alignItems="center" pb={3} pt={1}>
        <Button
          variant="contained"
          color="success"
          size="large"
          onClick={handleChat}
          startIcon={<HugeiconsIcon icon={WhatsappIcon} />}
        >
          Chat
        </Button>
      </Stack>

      <PhoneLoginDrawer />

      <OrderHistoryDrawer
        open={isHistoryDrawerOpen}
        onClose={() => setIsHistoryDrawerOpen(false)}
        currentOrderId={orderId}
        organizationId={organizationId}
        organizationAlias={organizationAlias}
        currency={(organization?.settings as any)?.currency || "NIO"}
      />
    </Stack>
  );
};

export default OrderStatusLayout;
